import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid"
import React, { useEffect, useState } from "react"
import { useMutation, useQuery } from "urql"

import PaymentProviderWrapper from "../../components/payments/PaymentProviderWrapper"
import { Button } from "../../components/shared/Buttons"
import CurrencyInput from "../../components/shared/CurrencyInput"
import { Label } from "../../components/shared/Inputs"
import { Banner, Flyout } from "../../components/shared/Layout"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { AnimatedModal } from "../../components/shared/Modal"
import { usePractice } from "../../contexts/PracticeContext"
import { formatPrice, unformatPrice } from "../../utils/utils"

import BookableEventPaymentModal from "./BookableEventPaymentModal"
import { canEditRegistrationPrice, getPaymentStatusDisplay } from "./bookableEventPaymentStatus"
import {
  PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION,
  REMOVE_CLIENT_FROM_BOOKABLE_EVENT_SERIES_MUTATION,
  UPDATE_BOOKABLE_EVENT_SERIES_REGISTRATION_AMOUNT_MUTATION
} from "./mutations"
import { BOOKABLE_EVENT_SERIES_REGISTRATION_QUERY } from "./queries"
import RegistrationPaymentActions from "./RegistrationPaymentActions"
import RequestEventRegistrationPaymentModal from "./RequestEventRegistrationPaymentModal"

export default function ProgramClientDetailsFlyout({
  visible,
  closeFlyout,
  client,
  bookableEventSeriesId,
  onClientRemoved
}) {
  const [removeError, setRemoveError] = useState(null)
  const [priceError, setPriceError] = useState(null)
  const [editAmountCents, setEditAmountCents] = useState(null)
  const [{ fetching: removeFetching }, removeClientFromSeries] = useMutation(
    REMOVE_CLIENT_FROM_BOOKABLE_EVENT_SERIES_MUTATION
  )
  const [{ fetching: updatePriceFetching }, updateRegistrationAmount] = useMutation(
    UPDATE_BOOKABLE_EVENT_SERIES_REGISTRATION_AMOUNT_MUTATION
  )
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showRequestPaymentModal, setShowRequestPaymentModal] = useState(false)
  const { practice } = usePractice()

  const [{ data: registrationData, fetching: registrationFetching, error: registrationError }, reexecuteQuery] =
    useQuery({
      query: BOOKABLE_EVENT_SERIES_REGISTRATION_QUERY,
      variables: {
        bookableEventSeriesId,
        clientId: client?.id
      },
      pause: !visible || !client?.id || !bookableEventSeriesId
    })

  const registration = registrationData?.bookableEventSeriesRegistration

  useEffect(() => {
    if (registration?.amountCents != null) {
      setEditAmountCents(registration.amountCents)
    }
  }, [registration?.amountCents])

  const formatCurrency = (amountCents) => `$${(amountCents / 100).toFixed(2)}`

  const canEditPrice = () => canEditRegistrationPrice(registration)

  const handleSavePrice = async () => {
    if (!client || editAmountCents == null) return

    setPriceError(null)
    try {
      const result = await updateRegistrationAmount({
        bookableEventSeriesId,
        clientId: client.id,
        amountCents: editAmountCents
      })

      if (result.data?.updateBookableEventSeriesRegistrationAmount?.result === "success") {
        reexecuteQuery({ requestPolicy: "network-only" })
      } else {
        const errorMessage =
          result.data?.updateBookableEventSeriesRegistrationAmount?.errors?.[0] || "Failed to update price"
        setPriceError(errorMessage)
      }
    } catch (err) {
      setPriceError(err.message || "Failed to update price")
    }
  }

  const paymentStatus = registration
    ? getPaymentStatusDisplay(registration.paymentStatus, { bookedBy: registration.bookedBy })
    : { label: "Unknown", color: "text-gray-dark" }

  const handleRemoveClient = async () => {
    if (!client || !bookableEventSeriesId) return

    setRemoveError(null)

    try {
      const result = await removeClientFromSeries({
        bookableEventSeriesId,
        clientId: client.id
      })

      if (result.data?.removeClientFromBookableEventSeries?.result === "success") {
        setShowRemoveConfirmation(false)
        onClientRemoved()
        closeFlyout()
      } else {
        const errorMessage =
          result.data?.removeClientFromBookableEventSeries?.errors?.[0] || "Failed to remove client from program"
        setRemoveError(errorMessage)
      }
    } catch (err) {
      setRemoveError(err.message || "An unexpected error occurred")
    }
  }

  const handlePaymentSuccess = () => {
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return (
    <>
      <Flyout header="Program Enrollee" visible={visible} closeFlyout={closeFlyout}>
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 font-semibold">Client Information</h3>
            <div className="rounded-lg border border-gray-light p-4">
              <div className="mb-2 flex items-center">
                <span className="font-medium">Name:</span>
                <span className="mx-1">
                  {client?.firstName} {client?.lastName}
                </span>
                <a
                  href={`/portal/clients/${client?.id}`}
                  className="ml-1 inline-flex items-center text-teal hover:text-teal-dark"
                  title="View client profile"
                  target="_blank"
                  rel="noopener noreferrer">
                  <ArrowTopRightOnSquareIcon className="size-5" />
                </a>
              </div>
              <div className="mb-2">
                <span className="font-medium">Email:</span> {client?.email || "N/A"}
              </div>
              <div>
                <span className="font-medium">Phone:</span> {client?.phone || "N/A"}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-2 font-semibold">Payment Status</h3>
            {registrationFetching ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : registrationError ? (
              <div className="rounded-lg border border-red p-4 text-red">Error loading payment information</div>
            ) : (
              <div className="rounded-lg border border-gray-light p-4">
                {!registration ? (
                  <div className="text-center italic text-gray-dark">Registration information not found</div>
                ) : (
                  <>
                    <div className="mb-2">
                      <span className="font-medium">Status:</span>
                      <span className={`ml-2 inline-block rounded-full px-2 py-0.5 ${paymentStatus.color}`}>
                        {paymentStatus.label}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium">Enrollment Date:</span>{" "}
                      {registration.createdAt ? new Date(registration.createdAt).toLocaleDateString() : "N/A"}
                    </div>
                    {registration.amountCents > 0 && !canEditPrice() && (
                      <div className="mb-2">
                        <span className="font-medium">Amount:</span> {formatCurrency(registration.amountCents)}
                      </div>
                    )}
                    {canEditPrice() && (
                      <div className="mb-4">
                        {registration.paymentStatus === "free" && (
                          <Banner type="info" className="mb-3">
                            This enrollment is Free ($0). Set the program price below, save, then Process or Request
                            payment.
                          </Banner>
                        )}
                        <Label htmlFor="series-registration-amount-edit">Amount for this client</Label>
                        <CurrencyInput
                          id="series-registration-amount-edit"
                          placeholder="Price"
                          value={
                            formatPrice(editAmountCents, practice?.defaultCurrency, { alwaysShowDecimals: true }) || ""
                          }
                          onChange={(e) => setEditAmountCents(unformatPrice(e.target.value))}
                        />
                        {priceError && <div className="mt-2 text-sm text-red">{priceError}</div>}
                        <Button
                          type="secondary"
                          className="mt-2 w-full"
                          onClick={handleSavePrice}
                          disabled={updatePriceFetching || editAmountCents === registration.amountCents}>
                          {updatePriceFetching ? "Saving..." : "Save price"}
                        </Button>
                      </div>
                    )}
                    {registration.discount && (
                      <>
                        <div className="mb-2">
                          <span className="font-medium">Discount Code:</span> {registration.discount.code}
                        </div>
                        <div className="mb-2">
                          <span className="font-medium">Discount Amount:</span>{" "}
                          {formatCurrency(registration.discount.amountCents)}
                        </div>
                      </>
                    )}

                    <RegistrationPaymentActions
                      registration={registration}
                      practice={practice}
                      isSeries
                      onPaymentUpdated={() => reexecuteQuery({ requestPolicy: "network-only" })}
                      onShowPaymentModal={() => setShowPaymentModal(true)}
                      onShowRequestPaymentModal={() => setShowRequestPaymentModal(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>

          <div className="pt-4">
            <Button type="destructive" onClick={() => setShowRemoveConfirmation(true)} className="w-full">
              Remove Client from Program
            </Button>
          </div>
        </div>
      </Flyout>

      <AnimatedModal
        visible={showRemoveConfirmation}
        hideModal={() => {
          setShowRemoveConfirmation(false)
          setRemoveError(null)
        }}
        header="Remove Client from Program"
        showFooter={true}
        cancelButtonCopy="Cancel"
        cancelButtonType="tertiary"
        actionButtonCopy={removeFetching ? "Removing..." : "Yes, Remove Client"}
        actionButtonType="destructive"
        saveDisabled={removeFetching}
        onSave={handleRemoveClient}>
        <div className="py-4">
          <p className="mb-4">
            Are you sure you want to remove{" "}
            <strong>
              {client?.firstName} {client?.lastName}
            </strong>{" "}
            from this program?
          </p>
          <p className="mb-4">They will be removed from all sessions in the program. This action cannot be undone.</p>
          {removeError && <div className="mt-4 rounded border border-red p-3 text-red">{removeError}</div>}
        </div>
      </AnimatedModal>

      {client && registration && (
        <PaymentProviderWrapper practice={practice} client={client}>
          <BookableEventPaymentModal
            visible={showPaymentModal}
            hideModal={() => setShowPaymentModal(false)}
            client={client}
            registration={registration}
            onPaymentSuccess={handlePaymentSuccess}
            processPaymentMutation={PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION}
            processPaymentField="processSeriesRegistrationPayment"
          />
        </PaymentProviderWrapper>
      )}

      {client && registration && (
        <RequestEventRegistrationPaymentModal
          visible={showRequestPaymentModal}
          hideModal={() => setShowRequestPaymentModal(false)}
          client={client}
          registration={registration}
          isSeries
        />
      )}
    </>
  )
}
